import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatMoney, fromMajor } from '@/lib/money'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'

const YIELD = 0.092
const FEE = 0.01

const RISKS = [
  'Rent can stop if the building sits empty, and there is no minimum payout.',
  'You cannot withdraw before completion except through the secondary market, where there may be no buyer.',
  'This is not a deposit and is not covered by deposit insurance.',
]

export function InvestReviewScreen() {
  const navigate = useNavigate()
  const [amount, setAmount] = useState('1000')
  const [agreed, setAgreed] = useState(false)

  const model = useMemo(() => {
    const parsed = Number.parseFloat(amount.replace(/,/g, ''))
    const principal = Number.isFinite(parsed) && parsed > 0 ? parsed : 0
    return {
      principal: fromMajor(principal, 'USD'),
      perYear: fromMajor(principal * (YIELD - FEE), 'USD'),
      perMonth: fromMajor((principal * (YIELD - FEE)) / 12, 'USD'),
    }
  }, [amount])

  return (
    <>
      <PageHeader
        title="Review your investment"
        subtitle="Torre Polanco · Mexico City · target yield 9.2%"
        actions={<Button variant="ghost" size="md" to="/real-assets">Cancel</Button>}
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">How much do you want to put in?</Text>
            <div className={`${styles.amountField} pb-mt-4`}>
              <Text variant="eyebrow" tone="secondary">From your US Dollar account · $18,240.50 available</Text>
              <div className={styles.amountRow}>
                <input
                  className={styles.amountInput}
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                  aria-label="Amount to invest"
                />
                <Text variant="subtitle" weight="semibold" tone="secondary">USD</Text>
              </div>
            </div>
            <Stack direction="row" gap={2} wrap className="pb-mt-4">
              {[500, 1000, 2500, 5000].map((preset) => (
                <Button key={preset} variant="outline" size="sm" onClick={() => setAmount(String(preset))}>
                  ${preset.toLocaleString('en-US')}
                </Button>
              ))}
            </Stack>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">Before you confirm</Text>
            <Stack gap={3} className="pb-mt-4">
              {RISKS.map((risk) => (
                <Stack key={risk} direction="row" gap={3} align="start">
                  <Icon name="warning" size={18} />
                  <Text variant="caption" tone="secondary">{risk}</Text>
                </Stack>
              ))}
            </Stack>
            <button
              type="button"
              onClick={() => setAgreed((value) => !value)}
              className="pb-mt-5"
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--pb-space-3)', width: '100%', textAlign: 'left' }}
              aria-pressed={agreed}
            >
              <Icon name={agreed ? 'check_box' : 'check_box_outline_blank'} size={22} filled={agreed} />
              <Text variant="caption" weight="semibold">
                I understand this is illiquid and I can lose what I put in.
              </Text>
            </button>
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">Rent, if it performs</Text>
              <Pill tone="accent">8.2% net</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              {formatMoney(model.perYear)}
            </Text>
            <Text variant="caption" tone="onDarkMuted">a year, paid monthly</Text>
            <div className={checkout.rule} />
            {[
              ['You put in', formatMoney(model.principal, { compactDecimals: true })],
              ['Target yield', '9.2%'],
              ['Platform fee', '1.0% of rent'],
              ['Rent per month', formatMoney(model.perMonth)],
            ].map(([label, value]) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">{label}</Text>
                <Text variant="caption" weight="semibold" tone="onDark" numeric>{value}</Text>
              </div>
            ))}
            <Card.Footer>
              <Button
                size="lg"
                block
                disabled={!agreed}
                iconEnd="arrow_forward"
                onClick={() => navigate('/real-assets/done', { replace: true })}
              >
                Invest {formatMoney(model.principal, { compactDecimals: true })}
              </Button>
              <Text variant="eyebrow" tone="onDarkMuted" className="pb-mt-3">
                Nothing moves until you confirm. Rent is a target, not a promise.
              </Text>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
